import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

import { InfiniteScrollCustomEvent, IonContent } from '@ionic/angular';
import { tap, Subscription, filter, catchError, of, concatMap, take, throttleTime } from 'rxjs';

import { CreateTogoRequest, Party, UpdateTogoRequest } from '@shared/party/models';
import { AuthUser } from '@shared/signup/models';
import { LabelsEnum } from '@shared/sms/enums';
import { isOnFreePlan } from '@shared/user/functions';
import { WebSocketEventsEnum } from '@shared/websocket';
import { PartyTypes } from '@shared/party';
import {
  PageTitleService,
  SearchBarComponent,
  SharedTableColumn,
  SharedTableComponent,
} from '@tablesready/common-ui';
import { FrontendModalService, FrontendModalSize } from '@tablesready/frontend-modal';
import { AppIcon, AppIconColor, AppIconSize } from '@tablesready/frontend-icons';
import { HeaderTitleService } from '@tablesready/frontend-header';
import { AuthRestaurantService, SocketService } from '@tablesready/frontend-store';
import { AlertsService, ClockService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { FrontendBreakpointsService } from '@tablesready/frontend-breakpoints';
import { TOGO_COLUMNS, TogoDataSource } from './togo-datasource';
import {
  MessageButtonService,
  MessageSenderService,
  TogoPartyService,
  WaitTimeService,
} from '../services';
import { AddEditTogoModalComponent } from './components';
import { ChatPartyComponent, NotifyActions, WaitlistActionsCallbacks } from '../components';
import { IWaitingParty } from '../waitlist/models';

@Component({
  selector: 'tablesready-togo',
  templateUrl: './togo.component.html',
  styleUrls: ['./togo.component.scss'],
  providers: [MessageButtonService],
})
export class TogoComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(IonContent) ionContent?: IonContent;
  @ViewChild('searchBarRef') searchBarRef?: SearchBarComponent;
  @ViewChild('nameColumn') nameColumnTemplate?: TemplateRef<unknown>;
  @ViewChild('actionsColumn') actionsColumnTemplate?: TemplateRef<unknown>;
  @ViewChild('notifyColumn') notifyColumnTemplate?: TemplateRef<unknown>;
  @ViewChild('waitColumn') waitColumnTemplate?: TemplateRef<unknown>;
  @ViewChild('quotedTimeColumn') quotedTimeColumnTemplate?: TemplateRef<unknown>;
  @ViewChild('togoTable', {
    static: true,
    read: SharedTableComponent,
  }) togoTableRef?: SharedTableComponent<Party>;

  dataSource = new TogoDataSource(this.service);
  columns: SharedTableColumn[] = [];
  isUserOnFreePlan = false;
  actionsCallbacks?: WaitlistActionsCallbacks<Party>;
  notifyActions?: NotifyActions<Party>;
  user?: AuthUser;

  now = 0;
  isMobile = false;
  isHandset = false;
  searchTerm = '';
  icon = AppIcon;
  pageSize = 10;
  iconSize = AppIconSize;
  iconColor = AppIconColor;
  noEstimatedWaitMessage: string = 'No wait';
  stopUpdateWaits: Subscription;
  waitingParties: IWaitingParty[];
  waitValues: number[] = [0, 0, 0, 0, 0];
  showEstimatedWait = false;
  onlyOneWaitValue: boolean = true;

  private infiniteScrollEvent?: InfiniteScrollCustomEvent;
  private readonly subscription = new Subscription();
  private readonly debouncePartiesMs = 200;

  constructor(
    private readonly service: TogoPartyService,
    private readonly socketService: SocketService,
    private readonly modalController: FrontendModalService,
    private readonly messageButtonService: MessageButtonService,
    private readonly alertsService: AlertsService,
    private readonly messageSenderService: MessageSenderService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly clockService: ClockService,
    private readonly waitTimeService: WaitTimeService,
    private readonly pageTitleService: PageTitleService,
    private readonly headerService: HeaderTitleService,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly frontendBreakpointService: FrontendBreakpointsService,
  ) {}

  ionViewWillEnter(): void {
    void this.ionContent?.scrollToTop(0);
  }

  onIonInfinite(ev: InfiniteScrollCustomEvent, table: SharedTableComponent<Party>): void {
    this.infiniteScrollEvent = ev;
    table.loadData({ search: this.searchTerm }, true);
  }

  dataLoaded(): void {
    if (!this.infiniteScrollEvent) {
      return;
    }

    void this.infiniteScrollEvent.target.complete();
    this.infiniteScrollEvent = null;
  }

  ionViewWillLeave(): void {
    this.togoTableRef?.sortBy('');
    this.togoTableRef?.loadData({}, true);

    if (!this.searchBarRef) {
      return;
    }

    this.searchBarRef.clearSearchField();
  }

  ngOnInit(): void {
    this.pageTitleService.setTitle('To go');
    this.headerService.setDataForRoute('togo', {
      title: 'To Go',
      badgeCount: 0,
    });

    const authSub = this.authRestaurantService.selectWithAccountAndLocation()
      .pipe(
        tap((who) => {
          this.user = who;
          this.isUserOnFreePlan = isOnFreePlan(who);
        }),
      ).subscribe();
    this.subscription.add(authSub);

    const countSubscription = this.dataSource.count$.asObservable()
      .pipe(tap((badgeCount) => {
        this.headerService.setDataForRoute('togo', { badgeCount });
      }))
      .subscribe();
    this.subscription.add(countSubscription);

    const mobileSub = this.frontendBreakpointService
      .observeXsBreakpoint()
      .subscribe((isMobile) => {
        this.isMobile = isMobile;
      });
    this.subscription.add(mobileSub);

    const handsetSub = this.breakpointObserver.observe([Breakpoints.Handset, Breakpoints.HandsetLandscape])
      .pipe(tap(({ matches: isHandset }) => this.isHandset = isHandset)).subscribe();
    this.subscription.add(handsetSub);

    //sockets
    const socketSub = this.socketService.getEventStream(WebSocketEventsEnum.parties)
      .pipe(
        throttleTime(this.debouncePartiesMs),
        tap(() => this.togoTableRef.loadData({}, true)),
      ).subscribe();
    this.subscription.add(socketSub);

    const timeSub = this.clockService.clock$
      .pipe(tap(now => this.now = now))
      .subscribe();
    this.subscription.add(timeSub);

    this.actionsCallbacks = {
      addEditParty: (party): void => {
        this.addEditParty(party);
      },
      confirmDelete: (party): void => {
        this.confirmDelete(party);
      },
      chatParty: (party): void => {
        this.chatParty(party);
      },
      checkPartyResources: (party): void => {
        this.markSeated(party);
      },
    };

    this.notifyActions = {
      smsPage: (party): void => {
        this.smsPage(party);
      },
      disableMessageButton: (party): boolean => this.disableMessageButton(party),
      mergeInboundOutbound: party => this.messageButtonService.mergeInboundOutbound(party),
    };
  }

  ngAfterViewInit(): void {
    this.columns = TOGO_COLUMNS(
      this.nameColumnTemplate,
      this.notifyColumnTemplate,
      this.actionsColumnTemplate,
      this.quotedTimeColumnTemplate,
      this.waitColumnTemplate,
    );
  }

  search(term: string, table: SharedTableComponent<Party>): void {
    this.searchTerm = term;
    table.loadData({ search: term }, true);
  }

  disableMessageButton(party: Party): boolean {
    if (!party.phone) {
      return true;
    }
    if (
      party.notifications?.[party.notifications.length - 1] === undefined
    ) {
      return false;
    }

    if (!this.user.location.messages.toGoOrderReady.enabled) {
      return true;
    }

    const msPerMin = 60000;

    // Disable if it's a ready message that was sent or is in process within last minute
    return (
      party.notifications[party.notifications.length - 1].label === LabelsEnum.toGoOrderReady &&
      ['inProcess', 'sent'].includes(party.notifications[party.notifications.length - 1].status_sent) &&
      party.notifications[party.notifications.length - 1].time >=
      this.now - msPerMin
    );
  }

  smsPage(party: Party): void {
    const readyMessage = this.user.location.messages.toGoOrderReady.message;
    this.messageSenderService.smsPage(party, readyMessage, LabelsEnum.toGoOrderReady);
  }

  addEditParty(party?: Party): void {
    const isEditMode = party !== undefined;
    let title = 'Add To go';
    let initialValues = {
      quoted_time: this.user.location.default_quoted_time,
      phone: '',
      customFields: {},
    };

    if (isEditMode) {
      title = 'Edit to go';
      initialValues = {
        name: party.name,
        phone: party.phone,
        note: party.note,
        quoted_time: Math.round((party.quoted_time - this.now) / 60000),
        messagingProvider: party.messagingProvider,
        customFields: party.customFields,
      };
    }

    this.modalController.create<AddEditTogoModalComponent, unknown>(
      AddEditTogoModalComponent,
      {
        user: {
          ...this.user.location,
          account: this.user.account,
        },
        title,
        initialValues,
      },
      FrontendModalSize.default,
      false,
    )
      .pipe(
        take(1),
        filter((data) => !!data),
        concatMap((payload: CreateTogoRequest) => {
          if (isEditMode) {
            return this.service.updateToGo(party._id, {
              name: payload.name,
              phone: payload.phone,
              note: payload.note,
              seated_time: party.seated_time,
              canceled_time: null,
              quoted_time: payload.quoted_time,
              messagingProvider: payload.messagingProvider,
            });
          }

          return this.service.createToGo(payload);
        }),
      )
      .subscribe(() => {
        this.togoTableRef.loadData({ }, true);
      });
  }

  ngOnDestroy(): void {
    this.messageButtonService.ngOnDestroy();
    this.subscription.unsubscribe();
  }

  updateWaitTimes() {
    if (!this.waitingParties) {
      return;
    }
    this.subscription.add(this.waitTimeService.getEstimates(this.waitingParties).subscribe(
      (data) => {
        this.waitingParties = data.waitingParties;
        this.waitValues = data.waitValues;
        this.onlyOneWaitValue = data.onlyOneWaitValue;
        this.showEstimatedWait = data.showEstimatedWait;
      },
      (error) => {
        if (error.message === 'error finding estimates in db') {
          this.noEstimatedWaitMessage = 'Not enough data';
        }
        this.stopUpdatingWaitsAction();
      },
    ));
  }

  stopUpdatingWaitsAction(): void {
    if (this.stopUpdateWaits) {
      this.stopUpdateWaits.unsubscribe();
      this.stopUpdateWaits = null;
    }
  }

  private confirmDelete(party: Party): void {
    const payload: UpdateTogoRequest = {
      name: party.name,
      phone: party.phone,
      note: party.note ? party.note : '',
      seated_time: party.seated_time,
      canceled_time: Date.now(),
    };

    const updateSub = this.service.updateToGo(party._id, payload)
      .pipe(
        catchError((err) => {
          const label = this.user.location.displayOptions?.labels?.customer;
          this.alertsService.addError(`${label || 'customer'} mark canceled failure.`);

          return of(err);
        }),
        tap(() => {
          this.alertsService.addInfo('Canceled.');
        }),
      )
      .subscribe(() => {
        this.togoTableRef.loadData({ }, true);
      });

    this.subscription.add(updateSub);
  }

  private markSeated(party: Party): void {
    const payload: UpdateTogoRequest = {
      name: party.name,
      phone: party.phone,
      note: party.note ? party.note : '',
      seated_time: new Date().getTime(),
      canceled_time: null,
    };

    const updateSub = this.service.updateToGo(party._id, payload)
      .pipe(
        catchError((err) => {
          const label = this.user.location.displayOptions?.labels?.customer;
          this.alertsService.add({
            type: IAlertTypes.danger,
            heading: 'Error:',
            body: `${label || 'customer'} mark as served failure.`,
          });

          return of(err);
        }),
        tap(() => {
          const label = this.user.location.displayOptions?.labels?.customer;
          this.alertsService.add({
            type: IAlertTypes.success,
            heading: 'Served:',
            body: `${party.name} ${label || 'customer'}`,
          });
        }),
      )
      .subscribe(() => {
        this.togoTableRef.loadData({ }, true);
      });

    this.subscription.add(updateSub);
  }

  private chatParty(party: Party): void {
    this.modalController.create<ChatPartyComponent, unknown>(ChatPartyComponent, {
      party,
      location: this.user.location,
      showReplyForm: true,
      showTemplates: this.user.account.features.savedReplies,
    })
      .pipe(take(1))
      .subscribe();
  }

  protected readonly location = location;
  protected readonly partyTypes = PartyTypes;
}
